import React, { useState, useMemo, useEffect } from 'react';
import Select from "react-select";
import styles from '../accountDetails.module.css';
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import { useAppSelector } from "../../../../services/hooks";
import { adminSelector } from "../../../../services/selectors/user.selectors";
import { planIdSelector } from "../../../../services/selectors/entitlements.selectors";
import {
  useGetAllPlansQuery,
  useGetAllFeaturesQuery,
  useGetPaymentsByCompanyIdQuery,
  useChangeSubscriptionPlanMutation
} from "../../../../services/api/utils.api";
import { useGetPaymentPeriodTypesQuery } from "../../../../services/api/onboarding.api";
import { useGetResourceCapsImpactQuery } from "../../../../services/api/resourceCaps.api";
import { IPayment, TPlan } from "../../../../services/types/utils.types";
import { TPaymentPeriodType } from "../../../../types/types";
import PlanCard from "../../../planCard/planCard";
import AiTokenUsage from "../../../AiTokenUsage/AiTokenUsage";
import Loader from "../../../loader/loader";
import Modal from "../../modal/Modal";
import GeneralSuccessPopup from "../../generalSuccessPopup/generalSuccessPopup";
import ErrorPopup from "../../errorPopup/errorPopup";
import PlanChangeImpactConfirmation from "../../planChangeImpactConfirmation/planChangeImpactConfirmation";
import OnboardingPaymentStep from "../../../../pages/onboarding/onboardingPaymentStep";

export const SubscriptionTab: React.FC = () => {
  const admin = useAppSelector(adminSelector);
  const entitlementsPlanId = useAppSelector(planIdSelector);

  const [showAllPlans, setShowAllPlans] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<TPlan | null>(null);
  const [selectedPaymentsPeriodTypesId, setSelectedPaymentsPeriodTypesId] = useState<number | null>(null);
  const [openModalSuccess, setOpenModalSuccess] = useState(false);
  const [openModalError, setOpenModalError] = useState<string | false>(false);
  const [openImpactConfirmation, setOpenImpactConfirmation] = useState(false);
  // Set from the impact preview once the submission it was shown for actually succeeds, so the
  // success receipt matches what the dialog just promised rather than dropping the seat-pack
  // context after submit.
  const [seatPacksEndingNotice, setSeatPacksEndingNotice] = useState(false);

  const {
    data: paymentPeriodTypes,
    isLoading: isLoadingPaymentPeriodTypes,
    isError: isErrorPaymentPeriodTypes,
  } = useGetPaymentPeriodTypesQuery();

  const { data: plans = [], isLoading: isPlansLoading } = useGetAllPlansQuery();
  const { data: allFeatures = [], isLoading: isFeaturesLoading } = useGetAllFeaturesQuery();
  
  const companyId = admin?.companyId ?? admin?.CompanyId ?? 0;
  const { data: paymentsResponse, isLoading: isPaymentsLoading } = useGetPaymentsByCompanyIdQuery(
    { type: 1, companyId },
    { skip: companyId <= 0 }
  );

  const [changeSubscriptionPlan, { isLoading: isChangingPlan }] = useChangeSubscriptionPlanMutation();

  const { data: impact, isFetching: isLoadingImpact } = useGetResourceCapsImpactQuery(
    {
      planId: selectedPlan?.id,
      plansPricesId: selectedPlan?.plansPricesId,
      paymentsPeriodTypesId: selectedPaymentsPeriodTypesId ?? undefined,
    },
    { skip: !openImpactConfirmation || !selectedPlan }
  );

  // Find active payment and plan.
  // A null activePayment means the company has no active recurring subscription (free plan),
  // so an upgrade must go through the "add" endpoint (type=1) rather than "update" (type=2).
  const activePayment = useMemo(() => {
    return paymentsResponse?.data?.find((payment: IPayment) =>
      payment.statusId === 1 && payment.paymentUid !== null
    ) ?? null;
  }, [paymentsResponse]);

  useEffect(() => {
    if (activePayment && selectedPaymentsPeriodTypesId === null) {
      setSelectedPaymentsPeriodTypesId(activePayment.paymentsPeriodTypesId ?? 1);
    }
  }, [activePayment, selectedPaymentsPeriodTypesId]);

  const activePlan = useMemo(() => {
    const planId = activePayment?.plansId ?? entitlementsPlanId;
    return plans.find(plan => plan.id === planId) ?? null;
  }, [plans, activePayment, entitlementsPlanId]);

  const displayedPlan = selectedPlan ?? activePlan;
  const hasChangedPlan = !!selectedPlan && selectedPlan.id !== activePlan?.id;

  const handleSelectPlan = (plan: TPlan) => {
    setSelectedPlan(plan);
    setShowAllPlans(false);
  };

  const handleCancelSelection = () => {
    setSelectedPlan(null);
  };

  const handlePaymentPeriodTypeChange = (option: any) => {
    setSelectedPaymentsPeriodTypesId(option?.value ?? null);
  };

  const handleSubmitPlanChange = async () => {
    if (!selectedPlan || !selectedPlan.plansPricesId) {
      setOpenModalError(t("MISSING_REQUIRED_DATA") || "Missing required data for plan change");
      return;
    }

    // Captured before the submission, not read back off state afterwards: this is the preview the
    // user actually confirmed, and setSelectedPlan(null) below would otherwise make it unreachable.
    const hadSeatPacksEnding = !!impact?.seatPacksEnding;

    try {
      await changeSubscriptionPlan({
        companyId,
        plansId: selectedPlan.id,
        plansPricesId: selectedPlan.plansPricesId,
        paymentsPeriodTypesId: selectedPaymentsPeriodTypesId ?? 1
      }).unwrap();

      // Reset selected plan after successful submission
      setSelectedPlan(null);
      setSeatPacksEndingNotice(hadSeatPacksEnding);
      setOpenModalSuccess(true);
    } catch (error: any) {
      const message = error?.data?.message || error?.message || t("PLAN_CHANGE_ERROR") || "Failed to change subscription plan";
      setOpenModalError(message);
    }
  };

  // Free company (no active recurring subscription) starting a paid plan: this must go
  // through the "add" endpoint (type=1), which requires new card details.
  const handleNewSubscriptionSuccess = () => {
    setSelectedPlan(null);
    // This flow never goes through the impact dialog, so any seat-pack notice left over from an
    // earlier plan-change attempt in this session must not bleed into this unrelated success popup.
    setSeatPacksEndingNotice(false);
    setOpenModalSuccess(true);
  };

  if (isPlansLoading || isFeaturesLoading || isPaymentsLoading) {
    return (
      <div className={styles.subscriptionContent}>
        <Loader />
      </div>
    );
  }
  if (showAllPlans) {
    return (
      <div className={styles.subscriptionContent}>
        <div className={styles.allPlansSection}>
       
          {plans.length > 0 && (
            <div className={styles.availablePlans}>
              <h3 className={`title title_blue`}>
                {t("AVAILABLE_PLANS") || t("SUBSCRIPTIONS")}
              </h3>
              <div className={styles.plansGrid}>
                {plans.map(plan => (
                  <div key={plan.id} className={styles.planCardWrapper}>
                    <PlanCard
                      plan={plan}
                      isSelected={displayedPlan?.id === plan.id}
                      onSelect={() => handleSelectPlan(plan)}
                      showGetStartedButton={false}
                      allFeatures={allFeatures}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <button 
            type="button" 
            className={styles.backToCurrentPlanButton} 
            onClick={() => setShowAllPlans(false)}
          >
            {t("BACK") || t("SHOW_CURRENT_PLAN")}
          </button>
        </div>
      </div>
    );
  }

  // No active plan view
  if (!activePlan && !selectedPlan) {
    return (
      <div className={styles.subscriptionContent}>
        <div className={styles.noActivePlanSection}>
          <p className={styles.noActivePlanMessage}>
            {t("NO_ACTIVE_PLAN") || t("NO_PLANS_AVAILABLE")}
          </p>
          
          {plans.length > 0 && (
            <div className={styles.availablePlans}>
              <h3 className={`title title_blue`}>
                {t("AVAILABLE_PLANS") || t("SUBSCRIPTIONS")}
              </h3>
              <div className={styles.plansGrid}>
                {plans.map(plan => (
                  <div key={plan.id} className={styles.planCardWrapper}>
                    <PlanCard
                      plan={plan}
                      isSelected={false}
                      onSelect={() => handleSelectPlan(plan)}
                      showGetStartedButton={false}
                      allFeatures={allFeatures}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.subscriptionContent}>
      <div className={styles.currentPlanSection}>
        <h4 className={`title title_blue`}>
          {hasChangedPlan
            ? t("YOUR_SELECTED_PLAN") || t("SELECTED_PLAN")
            : t("YOUR_CURRENT_PLAN") || t("CURRENT_PLAN")}
        </h4>
        <div className={styles.currentPlanCard}>
          {displayedPlan && (
            <PlanCard
              plan={displayedPlan}
              isSelected={true}
              showGetStartedButton={false}
              allFeatures={allFeatures}
            />
          )}
        </div>

        <AiTokenUsage />

        {hasChangedPlan && ((paymentPeriodTypes && paymentPeriodTypes.length > 0) || isLoadingPaymentPeriodTypes || isErrorPaymentPeriodTypes) && (
          <Select
            name="paymentsPeriodTypesId"
            value={paymentPeriodTypes?.find((ppt: TPaymentPeriodType) => ppt.id.toString() === selectedPaymentsPeriodTypesId?.toString()) ? {
              value: selectedPaymentsPeriodTypesId,
              label: paymentPeriodTypes.find((ppt: TPaymentPeriodType) => ppt.id.toString() === selectedPaymentsPeriodTypesId?.toString())?.name,
              id: selectedPaymentsPeriodTypesId
            } : null}
            onChange={handlePaymentPeriodTypeChange}
            options={paymentPeriodTypes?.map((ppt: TPaymentPeriodType) => ({
              value: ppt.id,
              label: ppt.name,
              id: ppt.id
            })) || []}
            placeholder={isErrorPaymentPeriodTypes ? t("ERROR_LOADING_DATA") : (t("ONBOARDING_SELECT") || "Select payment period")}
            isClearable={false}
            isLoading={isLoadingPaymentPeriodTypes}
            isDisabled={isErrorPaymentPeriodTypes}
            className={styles.select}
            classNamePrefix="react-select"
          />
        )}

        {hasChangedPlan && activePayment && (
          <div className={styles.actionButtons}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleCancelSelection}
              disabled={isChangingPlan}
            >
              {t("CANCEL")}
            </button>
            <button
              type="button"
              className={styles.submitButton}
              onClick={() => setOpenImpactConfirmation(true)}
              disabled={isChangingPlan}
            >
              {isChangingPlan ? t("LOADING") || "Loading..." : t("CHANGE_PLAN") || t("SUBMIT")}
            </button>
          </div>
        )}

        {hasChangedPlan && !activePayment && selectedPlan && selectedPaymentsPeriodTypesId && (
          <>
            <div className={styles.actionButtons}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={handleCancelSelection}
              >
                {t("CANCEL")}
              </button>
            </div>
            <OnboardingPaymentStep
              companyId={companyId}
              planId={selectedPlan.id}
              plansPricesId={selectedPlan.plansPricesId}
              paymentPeriodTypeId={selectedPaymentsPeriodTypesId}
              contactFullName={admin?.fullName}
              onPaymentSuccess={handleNewSubscriptionSuccess}
            />
          </>
        )}

        {!hasChangedPlan && plans.length > 0 && (
          <button
            type="button"
            className={styles.showAllPlansButton}
            onClick={() => setShowAllPlans(true)}
          >
            {t("SHOW_ALL_PLAN")}
          </button>
        )}
      </div>

      {openModalSuccess && (
        <Modal onClose={() => setOpenModalSuccess(false)}>
          <GeneralSuccessPopup
            onClose={() => setOpenModalSuccess(false)}
            infoText={seatPacksEndingNotice ? t("SEAT_PACKS_ENDING_SUCCESS_NOTE") : undefined}
          />
        </Modal>
      )}

      {openModalError && (
        <Modal onClose={() => setOpenModalError(false)}>
          <ErrorPopup
            text={typeof openModalError === "string" ? openModalError : undefined}
            onClose={() => setOpenModalError(false)}
          />
        </Modal>
      )}

      {openImpactConfirmation && selectedPlan && (
        <Modal onClose={() => setOpenImpactConfirmation(false)}>
          <PlanChangeImpactConfirmation
            onClose={() => setOpenImpactConfirmation(false)}
            onConfirm={() => {
              setOpenImpactConfirmation(false);
              handleSubmitPlanChange();
            }}
            planName={selectedPlan.name}
            impact={impact}
            isLoadingImpact={isLoadingImpact}
          />
        </Modal>
      )}
    </div>
  );
};